/**
 * DTO → view-model mapper for vocabulary-term diagnoses (OMIM, ORDO).
 *
 * Matches the stability contract in `patient.ts`. Unknown status values
 * narrow to ``'Suspected'`` so a defect in the server-side enum never
 * silently promotes a record to ``'Confirmed'`` in the UI.
 */

import type { components } from '@/api/client';
import type {
  DiagnosisStatus,
  DiagnosisViewModel,
  DiagnosisVocabulary,
} from '@/features/patient-record/types';

import { narrowEnum, nullableString } from '@/api/mappers/_shared';

type DiagnosisDto = components['schemas']['Diagnosis'];

const KNOWN_STATUS: ReadonlySet<DiagnosisStatus> = new Set<DiagnosisStatus>([
  'Confirmed',
  'Suspected',
  'Ruled Out',
  'Molecular Diagnosis',
]);

const KNOWN_VOCABULARY: ReadonlySet<DiagnosisVocabulary> =
  new Set<DiagnosisVocabulary>(['omim', 'ordo']);

/**
 * The vocabulary a prefixed term id belongs to, derived from its own prefix
 * (``ORDO:399`` → ``ordo``). The backend stores the vocabulary explicitly, so
 * this is only used where the vocabulary is not carried alongside the id — the
 * pedigree legend, whose entries are bare term ids — and as the mapper's
 * fallback when the server sends a vocabulary we do not know.
 *
 * Defaults to ``omim``: every diagnosis predating PTV2-1554 is OMIM, which is
 * also what the migration backfilled them to.
 */
export function vocabularyOfTermId(termId: unknown): DiagnosisVocabulary {
  if (typeof termId !== 'string') return 'omim';
  const prefix = termId.trim().split(':')[0]?.toLowerCase();
  return prefix !== undefined && KNOWN_VOCABULARY.has(prefix as DiagnosisVocabulary)
    ? (prefix as DiagnosisVocabulary)
    : 'omim';
}

/** Maps a single `Diagnosis` DTO to `DiagnosisViewModel`. */
export function mapDiagnosis(dto: DiagnosisDto): DiagnosisViewModel {
  return {
    id: dto.id,
    termId: dto.term_id,
    vocabulary: narrowEnum<DiagnosisVocabulary>(
      dto.vocabulary,
      KNOWN_VOCABULARY,
      vocabularyOfTermId(dto.term_id),
    ),
    title: dto.title,
    status: narrowEnum<DiagnosisStatus>(dto.status, KNOWN_STATUS, 'Suspected'),
    ageAtDiagnosis: nullableString(dto.age_at_diagnosis),
    notes: nullableString(dto.notes),
    createdAt: nullableString(dto.created_at),
  };
}

/** Maps a list of `Diagnosis` DTOs. */
export function mapDiagnoses(
  dtos: readonly DiagnosisDto[],
): DiagnosisViewModel[] {
  return dtos.map(mapDiagnosis);
}
